
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Container,
  Button,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import confetti from 'canvas-confetti';
import axios from 'axios';
import { BASE_URL } from '../constants';

const ThankYouPage = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const orderCode = searchParams.get('order_code');
  const isContact = searchParams.get('type') === 'contact';

  // Cart snapshot is available for some checkout flows.
  const cartItems = location.state?.cartItems || [];

  // Prevent duplicate tracking during this page lifecycle.
  const purchaseEventSent = useRef(false);
  const metaPurchaseEventSent = useRef(false);

  // Secure order details retrieved from the backend.
  const [orderDetails, setOrderDetails] = useState(null);
  const [, setOrderDetailsError] = useState(false);

  /*
   * INITIAL PAGE EFFECT
   */
  useEffect(() => {
    // Confetti animation.
    const end = Date.now() + 5000;
    const colors = ['#1e55ac', '#ffffff'];
    let animationFrame;

    const frame = () => {
      confetti({
        particleCount: 2,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors,
      });

      confetti({
        particleCount: 2,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors,
      });

      if (Date.now() < end) {
        animationFrame = requestAnimationFrame(frame);
      }
    };

    frame();

    /*
     * Fetch authenticated order details.
     * The backend response contains grand_total.
     */
    if (!isContact && orderCode) {
      const token = localStorage.getItem('token');

      if (token) {
        axios
          .get(`${BASE_URL}/api/v1/user/order/${orderCode}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          .then((response) => {
            if (response.data.success) {
              setOrderDetails(response.data.data);
            } else {
              setOrderDetailsError(true);
            }
          })
          .catch(() => {
            setOrderDetailsError(true);
          });
      } else {
        setOrderDetailsError(true);
      }
    }

    /*
     * META PIXEL CONTACT EVENT
     *
     * Purchase is handled separately after the
     * authenticated backend order total becomes available.
     */
    if (
      typeof window !== 'undefined' &&
      typeof window.fbq === 'function' &&
      isContact
    ) {
      window.fbq('track', 'Contact');
    }

    /*
     * TIKTOK PIXEL
     *
     * Existing behavior retained.
     */
    if (
      typeof window !== 'undefined' &&
      window.ttq &&
      typeof window.ttq.track === 'function'
    ) {
      if (isContact) {
        window.ttq.track('SubmitForm');
      } else if (orderCode) {
        window.ttq.track('PlaceAnOrder', {
          order_id: orderCode,
          currency: 'AED',
          value: cartItems.reduce(
            (sum, item) =>
              sum +
              Number(item.price || 0) *
                Number(item.quantity || 0),
            0
          ),
          contents: cartItems.map((item) => ({
            content_id: item.content_id,
            content_name: item.content_name,
            quantity: item.quantity,
            price: item.price,
          })),
        });
      }
    }

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isContact, orderCode]);

  /*
   * META PIXEL PURCHASE EVENT
   *
   * Uses the backend grand_total rather than a hardcoded zero.
   * Sends only after authenticated order details are loaded.
   */
  useEffect(() => {
    if (isContact || !orderCode || !orderDetails) {
      return;
    }

    if (metaPurchaseEventSent.current) {
      return;
    }

    if (
      typeof window === 'undefined' ||
      typeof window.fbq !== 'function'
    ) {
      return;
    }

    const orderTotal = Number(orderDetails.grand_total);

    if (!Number.isFinite(orderTotal) || orderTotal < 0) {
      return;
    }

    window.fbq('track', 'Purchase', {
      value: orderTotal,
      currency: 'AED',
      order_id: orderCode,
    });

    metaPurchaseEventSent.current = true;
  }, [isContact, orderCode, orderDetails]);

  /*
   * GA4 PURCHASE EVENT
   *
   * Existing cart-based behavior retained.
   * Network-payment fallback still requires verified
   * backend order-item mapping.
   */
  useEffect(() => {
    if (isContact || !orderCode) {
      return;
    }

    if (purchaseEventSent.current) {
      return;
    }

    if (cartItems.length > 0) {
      const purchaseValue = cartItems.reduce(
        (sum, item) =>
          sum +
          Number(item.price || 0) *
            Number(item.quantity || 0),
        0
      );

      const items = cartItems.map((item) => ({
        item_id: item.content_id,
        item_name: item.content_name,
        price: Number(item.price || 0),
        quantity: Number(item.quantity || 0),
      }));

      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: 'purchase',
        ecommerce: {
          transaction_id: orderCode,
          value: purchaseValue,
          currency: 'AED',
          items,
        },
      });

      purchaseEventSent.current = true;

      return;
    }

    // Backend order-item mapping has not yet been verified.
    // Do not send an incomplete GA4 Purchase event.
    if (orderDetails) {
      return;
    }
  }, [isContact, orderCode, cartItems, orderDetails]);

  /*
   * GOOGLE CUSTOMER REVIEWS
   */
  useEffect(() => {
    if (isContact || !orderCode || !orderDetails) {
      return;
    }

    const email = orderDetails?.user?.email;
    const estimatedDeliveryDate = orderDetails?.delivery_date;

    if (!email || !estimatedDeliveryDate) {
      return;
    }

    let checkGoogleAPI;
    let timeoutId;

    const initializeGoogleReviews = () => {
      if (
        typeof window !== 'undefined' &&
        window.gapi &&
        typeof window.gapi.load === 'function'
      ) {
        window.gapi.load('surveyoptin', () => {
          if (
            window.gapi.surveyoptin &&
            typeof window.gapi.surveyoptin.render === 'function'
          ) {
            window.gapi.surveyoptin.render({
              merchant_id: 5819063391,
              order_id: orderCode,
              email,
              delivery_country: 'AE',
              estimated_delivery_date: estimatedDeliveryDate,
            });
          }
        });
      }
    };

    const scriptSelector =
      'script[src="https://apis.google.com/js/platform.js?onload=renderOptIn"]';

    const existingScript = document.querySelector(scriptSelector);

    if (existingScript) {
      if (window.gapi && window.gapi.load) {
        initializeGoogleReviews();
      } else {
        checkGoogleAPI = setInterval(() => {
          if (window.gapi && window.gapi.load) {
            clearInterval(checkGoogleAPI);
            initializeGoogleReviews();
          }
        }, 100);

        timeoutId = setTimeout(() => {
          clearInterval(checkGoogleAPI);
        }, 10000);
      }

      return () => {
        if (checkGoogleAPI) {
          clearInterval(checkGoogleAPI);
        }

        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }

    window.renderOptIn = initializeGoogleReviews;

    const script = document.createElement('script');
    script.src =
      'https://apis.google.com/js/platform.js?onload=renderOptIn';
    script.async = true;
    script.defer = true;

    document.head.appendChild(script);

    return () => {
      if (checkGoogleAPI) {
        clearInterval(checkGoogleAPI);
      }

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      if (window.renderOptIn === initializeGoogleReviews) {
        delete window.renderOptIn;
      }
    };
  }, [isContact, orderCode, orderDetails]);

  /*
   * PAGE UI
   */
  return (
    <Container
      maxWidth="sm"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        textAlign: 'center',
      }}
    >
      <Card
        sx={{
          padding: 4,
          boxShadow:
            `rgba(50, 50, 93, 0.25) 0px 50px 100px -20px,
             rgba(0, 0, 0, 0.3) 0px 30px 60px -30px,
             rgba(10, 37, 64, 0.35) 0px -2px 6px 0px inset`,
          borderRadius: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* SUCCESS CHECKMARK */}
          <div className="success-checkmark">
            <div className="check-icon">
              <span className="icon-line line-tip"></span>
              <span className="icon-line line-long"></span>
              <div className="icon-circle"></div>
              <div className="icon-fix"></div>
            </div>
          </div>

          {isContact ? (
            <>
              <Typography
                className="poppins"
                variant="h4"
                gutterBottom
              >
                Thank You!
              </Typography>

              <CardContent>
                <Typography
                  className="poppins"
                  variant="h6"
                  gutterBottom
                >
                  Your message has been received.
                </Typography>

                <Typography
                  className="poppins"
                  variant="body1"
                  gutterBottom
                  sx={{
                    color: 'text.secondary',
                    mb: 2,
                  }}
                >
                  Our team will get back to you as soon as possible.
                  We appreciate you reaching out to BuraqStar.
                </Typography>

                <Button
                  variant="contained"
                  component={Link}
                  to="/"
                  className="bg-[#1E55AC] w-full sm:w-[40%] mx-5 mt-5 py-2.5 text-white poppins capitalize"
                  startIcon={<HomeOutlinedIcon />}
                >
                  Back to Home
                </Button>
              </CardContent>
            </>
          ) : (
            <>
              <Typography
                className="poppins"
                variant="h4"
                gutterBottom
              >
                {t('thankyou.title')}
              </Typography>

              <CardContent>
                <Typography
                  className="poppins"
                  variant="h6"
                  gutterBottom
                >
                  {t('thankyou.order_placed')}
                </Typography>

                <Typography
                  className="poppins"
                  variant="h6"
                  gutterBottom
                >
                  {t('thankyou.order_code')}{' '}
                  <strong>{orderCode}</strong>
                </Typography>

                <Typography
                  className="poppins"
                  variant="body1"
                  gutterBottom
                >
                  {t('thankyou.message')}
                </Typography>

                <Button
                  variant="contained"
                  component={Link}
                  to="/user/my-order"
                  className="bg-[#1E55AC] w-full sm:w-[40%] mx-5 mt-5 py-2.5 text-white poppins capitalize"
                  endIcon={<ArrowForwardIcon />}
                >
                  {t('thankyou.go_to_orders')}
                </Button>
              </CardContent>
            </>
          )}
        </Box>
      </Card>
    </Container>
  );
};

export default ThankYouPage;
